/**
 * Symbols tab — category list on the left, tile grid on the right.
 *
 * Non-negotiable from the user: NO horizontal scrollbars on any
 * category grid (notably Sets and Arrows, which have wider glyphs
 * than Greek/Ops). The grid uses
 *
 *   grid-template-columns: repeat(auto-fit, minmax(46px, 1fr))
 *
 * so tiles wrap to the next row whenever the drawer narrows. The
 * tile width never goes below 46px (legible glyph + the tiny mono
 * command label). `overflow-x: hidden` on the panel guarantees no
 * horizontal scroll under any container width.
 */
import { useMemo, useRef, useState } from "react";
import { CAT_COLORS, type CategoryKey, type SymbolEntry, PAL, isLowercaseGreek } from "./palette";
import { T, type Token } from "./tokens";
import { CommonThemeRail } from "./CommonThemeRail";
import { buildCommonItems, THEMES, type ThemeId } from "./commonThemes";

interface SymbolsTabProps {
  onInsert: (token: Token) => void;
  /** Most-recent symbol entries (deduped by `.c`, max 8). */
  recent: SymbolEntry[];
  /** Show the LaTeX command under each tile glyph. */
  showShortcuts: boolean;
  /** Active Common-panel theme id. */
  commonTheme: ThemeId;
  /** Handler invoked when the user picks a different theme. */
  onChangeCommonTheme: (theme: ThemeId) => void;
}

interface CatRow {
  key: CategoryKey | "common" | "history";
  label: string;
  glyph: string;
  italicGlyph?: boolean;
  ink: string;
  soft: string;
  mid: string;
  items: ItemDef[];
}

interface ItemDef {
  g: string;
  c: string;
  n: string;
  italic: boolean;
  /** Builds the token to insert. */
  mk: () => Token;
}

/**
 * Curated "Common" panel — 24 tiles ordered for a 6×4 reading grid,
 * swappable across 8 STEM themes via the `CommonThemeRail`.
 *
 * Tile lists live in `commonThemes.ts`. The General theme matches
 * Q10's final recommendation from
 * `lilia-docs/product/2026-05-28-math-editor-commons-results.md`;
 * the seven themed variants (Calculus / Linear algebra / Stats /
 * Discrete / Sets / Physics / CS) are curated per Q4 of the same
 * research, in the same 6×4 reading order.
 *
 * The first row holds the cross-corpus core (`=` `+` `−` `·` `a⁄b`
 * `√x`) across every theme — switching themes never costs muscle
 * memory for the most common authoring moves.
 */

function buildCats(): CatRow[] {
  const out: CatRow[] = [];
  (Object.keys(PAL) as Array<keyof typeof PAL>).forEach((k) => {
    const cat = CAT_COLORS[k];
    const items = PAL[k].map<ItemDef>((entry) => {
      const italic = k === "greek" && isLowercaseGreek(entry.g);
      const mk: () => Token = () => {
        switch (k) {
          case "greek":  return italic ? T.greek(entry.g) : T.upletter(entry.g);
          case "ops":    return T.op(entry.g);
          case "rel":    return T.rel(entry.g);
          case "bigops": return T.bigop(entry.g);
          case "arrows": return T.arrow(entry.g);
          case "fn":     return T.fn(entry.g);
          case "logic":  return T.op(entry.g);
          case "sets":   return T.upletter(entry.g);
          default:       return T.letter(entry.g);
        }
      };
      return { g: entry.g, c: entry.c, n: entry.n, italic, mk };
    });
    const glyph =
      k === "greek"  ? "α" :
      k === "ops"    ? "⊕" :
      k === "rel"    ? "≤" :
      k === "bigops" ? "∫" :
      k === "arrows" ? "→" :
      k === "fn"     ? "fn" :
      k === "logic"  ? "∀" :
      k === "sets"   ? "ℝ" : "?";
    out.push({
      key: k, label: cat.name, glyph, italicGlyph: k === "greek",
      ink: cat.ink, soft: cat.soft, mid: cat.mid,
      items,
    });
  });
  return out;
}

export function SymbolsTab({
  onInsert, recent, showShortcuts, commonTheme, onChangeCommonTheme,
}: SymbolsTabProps) {
  const cats = useMemo(() => buildCats(), []);
  const activeTheme = THEMES.find((t) => t.id === commonTheme) ?? THEMES[0];

  // Common — curated most-used items, swapped by the theme rail.
  // The glyph reflects the active theme so the category column also
  // signals "which Common am I looking at?"
  const commonRow: CatRow = useMemo(() => ({
    key: "common",
    label: "Common",
    glyph: activeTheme.glyph,
    ink: "var(--lilia-primary)",
    soft: "color-mix(in srgb, var(--lilia-primary) 11%, transparent)",
    mid: "color-mix(in srgb, var(--lilia-primary) 22%, transparent)",
    items: buildCommonItems(commonTheme),
  }), [commonTheme, activeTheme.glyph]);

  // History — recently-inserted items (deduped, capped at 8). Always
  // visible so users see it's a thing; renders an empty-state
  // message when there's nothing yet.
  const historyRow: CatRow = useMemo(() => ({
    key: "history",
    label: "History",
    glyph: "↻",
    ink: "var(--lilia-text)",
    soft: "rgba(31,26,23,0.05)",
    mid: "rgba(31,26,23,0.12)",
    items: recent.map<ItemDef>((entry) => ({
      g: entry.g, c: entry.c, n: entry.n, italic: isLowercaseGreek(entry.g),
      mk: () => {
        // Recents don't remember their original category; pick the
        // best-fit factory by looking the symbol up. Falls back to a
        // plain letter token if we can't match.
        for (const k of Object.keys(PAL) as Array<keyof typeof PAL>) {
          const hit = PAL[k].find((s) => s.c === entry.c);
          if (hit) {
            switch (k) {
              case "greek":  return isLowercaseGreek(hit.g) ? T.greek(hit.g) : T.upletter(hit.g);
              case "ops":    return T.op(hit.g);
              case "rel":    return T.rel(hit.g);
              case "bigops": return T.bigop(hit.g);
              case "arrows": return T.arrow(hit.g);
              case "fn":     return T.fn(hit.g);
              case "logic":  return T.op(hit.g);
              case "sets":   return T.upletter(hit.g);
            }
          }
        }
        return T.letter(entry.g);
      },
    })),
  }), [recent]);

  // Common always pinned first; History second; the standard
  // categories follow. History stays in the list even when empty so
  // the user discovers the feature — the grid renders an empty-state
  // message when there's nothing recent yet.
  const allRows = useMemo<CatRow[]>(
    () => [commonRow, historyRow, ...cats],
    [commonRow, historyRow, cats],
  );

  const [activeKey, setActiveKey] = useState<CategoryKey | "common" | "history">(allRows[0].key);
  const [query, setQuery] = useState("");
  const [hover, setHover] = useState<ItemDef | null>(null);

  const active = allRows.find((c) => c.key === activeKey) ?? allRows[0];
  const filtered = useMemo(() => {
    if (!query) return active.items;
    const q = query.toLowerCase().replace(/^[\\/]/, "");
    return active.items.filter((it) =>
      it.c.toLowerCase().includes(q) || it.n.toLowerCase().includes(q),
    );
  }, [active, query]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Common-panel theme rail — sits between the tab strip (above
          this component in the drawer) and the search bar. Always
          rendered; switching themes immediately re-renders the
          24-tile Common grid. Only the Common category is affected;
          all other categories ignore the theme. */}
      <CommonThemeRail activeTheme={commonTheme} onChange={onChangeCommonTheme} />
      <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--lilia-border)" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          height: 30, padding: "0 10px",
          background: "var(--lilia-surface)",
          border: "1px solid var(--lilia-border)",
          borderRadius: 6,
        }}>
          <span aria-hidden style={{ color: "var(--lilia-text-muted)" }}>🔍</span>
          <input
            type="text"
            placeholder={`Search ${active.items.length} symbols, names, commands`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              flex: 1, border: 0, outline: "none", background: "transparent",
              fontSize: 12, color: "var(--lilia-text)",
            }}
          />
          <KbdMini>⌘F</KbdMini>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
        {/* Category column */}
        <div
          style={{
            width: 132, flexShrink: 0,
            borderRight: "1px solid var(--lilia-border)",
            overflowY: "auto", overflowX: "hidden",
            padding: "8px 0",
          }}
        >
          {allRows.map((cat) => {
            const isActive = cat.key === activeKey;
            return (
              <button
                key={cat.key}
                type="button"
                data-testid={`math-symbol-cat-${cat.key}`}
                data-active={isActive ? "1" : "0"}
                onClick={() => setActiveKey(cat.key)}
                style={{
                  width: "100%",
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "7px 10px",
                  background: isActive ? "var(--lilia-surface)" : "transparent",
                  borderLeft: `2px solid ${isActive ? cat.ink : "transparent"}`,
                  borderRight: 0, borderTop: 0, borderBottom: 0,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    width: 22, height: 22, borderRadius: 4,
                    background: cat.soft, color: cat.ink,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "var(--lilia-font-serif, Georgia, serif)",
                    fontSize: 13,
                    fontStyle: cat.italicGlyph ? "italic" : "normal",
                  }}
                >
                  {cat.glyph}
                </span>
                <span style={{
                  flex: 1,
                  fontSize: 12,
                  color: isActive ? "var(--lilia-text)" : "var(--lilia-text-secondary)",
                  fontWeight: isActive ? 600 : 500,
                  display: "flex", alignItems: "baseline", gap: 5,
                  minWidth: 0,
                }}>
                  <span>{cat.label}</span>
                  {/* "COMMON · Calculus" — theme name in primary ink,
                      mirroring Claude Design's section-header spec.
                      Hidden when General because no extra info to convey. */}
                  {cat.key === "common" && activeTheme.id !== "general" && (
                    <span
                      style={{
                        color: "var(--lilia-primary)",
                        fontSize: 10.5, fontWeight: 600,
                        whiteSpace: "nowrap",
                        overflow: "hidden", textOverflow: "ellipsis",
                        minWidth: 0,
                      }}
                      title={activeTheme.name}
                    >· {activeTheme.chip}</span>
                  )}
                </span>
                <span style={{
                  fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)",
                  fontSize: 9.5,
                  color: "var(--lilia-text-tertiary)",
                }}>
                  {cat.items.length}
                </span>
              </button>
            );
          })}
        </div>

        {/* Grid panel — overflow-x hidden so the grid wraps instead of scrolling. */}
        <div
          style={{
            flex: 1, minWidth: 0,
            display: "flex", flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              flex: 1, minHeight: 0,
              overflowY: "auto", overflowX: "hidden",
              padding: 8,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(46px, 1fr))",
                gap: 6,
                alignContent: "start",
              }}
            >
              {filtered.map((it) => (
                <SymbolTile
                  key={`${active.key}-${it.c}-${it.g}`}
                  item={it}
                  ink={active.ink}
                  soft={active.soft}
                  mid={active.mid}
                  showShortcut={showShortcuts}
                  onInsert={() => onInsert(it.mk())}
                  onHover={setHover}
                />
              ))}
            </div>
            {filtered.length === 0 && (
              <p style={{ padding: 16, fontSize: 12, color: "var(--lilia-text-muted)", textAlign: "center", lineHeight: 1.6 }}>
                {active.key === "history" && !query
                  ? <>Nothing here yet. Pick a symbol from <strong>Common</strong> or any other category — it'll appear here for quick re-use.</>
                  : <>No symbols match "<strong>{query}</strong>".</>}
              </p>
            )}
          </div>

          {/* Hover info bar */}
          <div
            style={{
              height: 32,
              borderTop: "1px solid var(--lilia-border)",
              background: "var(--lilia-surface)",
              padding: "0 12px",
              display: "flex", alignItems: "center", gap: 10,
              fontSize: 11,
              color: "var(--lilia-text-secondary)",
            }}
          >
            {hover ? (
              <>
                <span style={{
                  fontFamily: "var(--lilia-font-serif, Georgia, serif)",
                  fontSize: 16, fontStyle: hover.italic ? "italic" : "normal",
                  color: active.ink,
                }}>
                  {hover.g}
                </span>
                <span style={{ fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)" }}>{hover.c}</span>
                <span style={{ color: "var(--lilia-text-muted)" }}>· {hover.n}</span>
              </>
            ) : (
              <span style={{ color: "var(--lilia-text-muted)" }}>
                Hover a symbol to see its command · all categories are colour-coded
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SymbolTile({
  item, ink, soft, mid, showShortcut, onInsert, onHover,
}: {
  item: ItemDef; ink: string; soft: string; mid: string;
  showShortcut: boolean;
  onInsert: () => void;
  onHover: (it: ItemDef | null) => void;
}) {
  const [hovered, setHovered] = useState(false);
  // Post-click "key press" flash — a brief strong highlight that lingers
  // ~450ms so the pressed symbol is visible in the recorded build video
  // and per-step capture screenshots (calculator feel).
  const [flash, setFlash] = useState(false);
  const flashTimer = useRef<number | undefined>(undefined);
  const handleClick = () => {
    setFlash(true);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(false), 450);
    onInsert();
  };
  return (
    <button
      type="button"
      data-testid="math-symbol-tile"
      data-symbol-cmd={item.c}
      data-symbol-glyph={item.g}
      data-pressed={flash ? "true" : undefined}
      onClick={handleClick}
      onMouseEnter={() => { setHovered(true); onHover(item); }}
      onMouseLeave={() => { setHovered(false); onHover(null); }}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: 2, padding: "8px 4px 6px",
        minHeight: 54,
        background: flash ? "color-mix(in srgb, var(--lilia-primary) 22%, transparent)" : hovered ? soft : "var(--lilia-surface)",
        border: `1px solid ${flash ? "color-mix(in srgb, var(--lilia-primary) 65%, transparent)" : hovered ? mid : "var(--lilia-border)"}`,
        boxShadow: flash ? "0 0 0 3px color-mix(in srgb, var(--lilia-primary) 35%, transparent)" : "none",
        borderRadius: 6,
        cursor: "pointer",
        transition: "background 120ms, border-color 120ms, box-shadow 120ms, transform 80ms",
        transform: flash ? "scale(1.06)" : "none",
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontFamily: "var(--lilia-font-serif, 'IBM Plex Serif', Georgia, serif)",
          fontSize: 19,
          fontStyle: item.italic ? "italic" : "normal",
          color: ink,
          lineHeight: 1,
        }}
      >
        {item.g}
      </span>
      {showShortcut && (
        <span
          style={{
            fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)",
            fontSize: 8.5,
            color: "var(--lilia-text-muted)",
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.c}
        </span>
      )}
    </button>
  );
}

function KbdMini({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)",
      fontSize: 10, fontWeight: 600,
      background: "var(--lilia-bg-muted)",
      border: "1px solid var(--lilia-border)",
      padding: "0 4px", borderRadius: 3,
      color: "var(--lilia-text-secondary)",
    }}>{children}</span>
  );
}
